<template>
  <div class="flex flex-col-reverse lg:flex-col gap-y-4 lg:w-[298px] lg:h-full">
    <!-- Creator Info -->
    <div class="creator-info p-4 bg-gray-500 rounded-xl">
      <!-- Row 1: Avatar and Creator Info -->
      <div class="flex gap-4 mb-4">
        <!-- Left: Creator Avatar -->
        <div class="flex-shrink-0">
          <img
            v-if="!avatarLoadError && creatorData.avatar_url"
            :src="creatorData.avatar_url"
            :alt="creatorData.brand_name + ' Avatar'"
            class="w-12 h-12 lg:w-16 lg:h-16 rounded-full bg-gray-600"
            @error="avatarLoadError = true"
          >
          <div
            v-else
            class="w-12 h-12 lg:w-16 lg:h-16 rounded-full bg-gray-600 flex items-center justify-center text-white font-medium text-14px lg:text-18px"
          >
            {{ creatorData.initials }}
          </div>
        </div>

        <!-- Right: Creator Name and Project Count -->
        <div class="flex-1">
          <div class="text-14px lg:text-16px font-medium text-white mb-1">
            {{ creatorData.brand_name }}
          </div>
          <div class="text-12px text-blue-10 flex items-center gap-1">
            {{ creatorData.projects_count }}  {{ $t('campaigns.show.created') }}
            <span class="ml-1 icon location w-4 h-4 grayscale opacity-40"></span>
            <span class="text-gray-200"> {{ campaignData.country }}</span>
          </div>
        </div>
      </div>

      <!-- Row 2: Creator Description -->
      <div class="mb-4">
        <div class="text-12px text-gray-150 leading-relaxed">
          {{ creatorData.description }}
        </div>
      </div>

      <!-- Row 3: Social Links and Message Button -->
      <div>
        <!-- Left: Social Links -->
        <div class="flex gap-2">
          <a
            v-for="social in creatorData.social_links"
            :key="social.platform"
            :href="social.url"
            target="_blank"
            class="block"
          >
            <span :class="`inline-block icon ${social.platform === 'portfolio' ? 'website' : `profile_${social.platform}_url`} w-4 h-4 lg:w-5 lg:h-5 align-middle`"></span>
          </a>
        </div>

        <!-- Right: Message Button -->
        <button @click="navigateToComments" class="mt-4 w-full px-3 py-1 lg:px-4 lg:py-1 border text-10px lg:text-12px rounded-full flex justify-center items-center gap-1 lg:gap-2">
          <span class="icon inbox w-4 h-4 lg:w-6 lg:h-6"></span>
            {{ $t('campaigns.show.comment') }}
        </button>
      </div>
    </div>

    <!-- Rewards Section -->
    <div class="rewards-section p-4 bg-gray-500 rounded-xl lg:sticky lg:top-[80px] lg:max-h-[calc(100vh-100px)] lg:flex lg:flex-col lg:overflow-hidden">
      <div class="text-14px lg:text-16px font-medium mb-4 text-white lg:flex-shrink-0">
        {{ $t('campaigns.show.rewards') }} ({{ rewardsData.length }})
      </div>

      <!-- Campaign Status Banner (移到最上面) -->
      <div v-if="campaignStatus.message" class="mb-4 p-3 rounded-lg text-center lg:flex-shrink-0" :class="campaignStatus.cssClass">
        <div class="text-14px font-medium">{{ campaignStatus.message }}</div>
        <div v-if="campaignStatus.detail" class="text-12px mt-1 opacity-80">{{ campaignStatus.detail }}</div>
      </div>
      <div class="flex flex-col sm:flex-row lg:flex-col flex-wrap lg:flex-nowrap gap-4 lg:gap-y-6 lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:pr-1 md:scrollbar">
        <template v-for="(reward, index) in rewardsData" :key="reward.id">
          <hr v-if="index > 0" class="my-0 border-[#6E747C] sm:hidden lg:block">
          <div class="reward rounded-lg flex-1 min-w-[236px] w-full flex flex-col">
            <div class="mb-3">
              <img
                v-if="getRewardImageUrl(reward)"
                :src="getRewardImageUrl(reward)"
                :alt="reward.title"
                class="w-full h-[238px] smd:object-cover object-contain rounded-lg"
                @error="handleImageError"
              >
              <div
                v-else
                class="w-full h-[238px] bg-gray-600 rounded-lg flex items-center justify-center text-gray-400"
              >
                <span class="text-14px">
                  {{ $t('campaigns.show.no_image') }}
                </span>
              </div>
            </div>
            <div class="text-18px font-medium text-white break-word">{{ reward.title }}</div>
            <div class="text-18px font-medium text-white">{{ formatCurrency(reward.price) }}</div>
            <div class="text-14px text-gray-150 mt-2 flex-1" v-html="reward.description.replace(/\n/g, '<br>')"></div>
            <div class="grid grid-cols-2 gap-x-4 mt-2 text-grey-8">
              <div>
                <div class="text-12px">
                  {{ $t('campaigns.show.backers') }}
                </div>
                <div class="text-16px">{{ reward.sold_quantity || 0 }}</div>
              </div>
              <div>
                <div class="text-12px">
                  {{ $t('campaigns.show.estimated_delivery') }}
                </div>
                <div class="text-16px">{{ formatDate(reward.estimated_delivery_at) }}</div>
              </div>
            </div>
            <div v-if="canPledge" class="flex items-center justify-between mt-3">
              <!-- Quantity Selector -->
              <div class="w-full flex justify-between items-center rounded-lg border border">
                <button
                  @click="decrementQuantity(reward.id)"
                  :disabled="shouldDisableControls || getRewardQuantity(reward.id) <= 0"
                  class="px-3 py-2 text-white hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-l-lg text-16px font-medium"
                >
                  −
                </button>
                <input
                  :id="`reward-quantity-${reward.id}`"
                  :name="`reward_quantity_${reward.id}`"
                  type="number"
                  :value="getRewardQuantity(reward.id)"
                  @input="updateQuantity(reward.id, $event.target.value)"
                  :disabled="shouldDisableControls"
                  class="w-16 px-2 py-2 bg-transparent text-center text-white text-14px border-0 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  min="0"
                  readonly
                >
                <button
                  @click="incrementQuantity(reward.id)"
                  :disabled="shouldDisableControls"
                  class="px-3 py-2 text-white hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-r-lg text-16px font-medium"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Pledge Button (desktop only — mobile uses MobilePledgeBar) -->
      <pledge-button class="mt-4 w-full hidden lg:flex lg:flex-shrink-0" />
    </div>
  </div>
</template>

<script>
import { computed, ref, watch, inject } from 'vue'
import { formatCurrency } from '../../../../components/exchange_rates.js'

export default {
  name: 'CampaignSidebar',
  props: {
    campaignData: {
      type: [Object, null],
      default: null
    },
    rewardsData: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    // Avatar 載入錯誤狀態
    const avatarLoadError = ref(false)

    // 從父組件注入數量管理功能
    const updateRewardQuantity = inject('updateRewardQuantity')

    // 從父組件注入 disable 控制邏輯，提供安全的預設值
    const shouldDisableControls = inject('shouldDisableControls', computed(() => false))

    // 從父組件注入狀態檢查
    const isCampaignEnded = inject('isCampaignEnded', computed(() => false))
    const isPreLaunchCampaign = inject('isPreLaunchCampaign', computed(() => false))
    const canPledge = inject('canPledge', computed(() => false))

    // 從父組件注入翻譯函數
    const t = inject('t', (key) => key)

    // 從父組件注入 reward quantities
    const injectedRewardQuantities = inject('rewardQuantities', computed(() => ({})))

    // Campaign 狀態訊息（合併為單一 computed）
    const campaignStatus = computed(() => {
      if (isCampaignEnded.value) {
        return {
          message: t('campaigns.show.status.ended'),
          detail: t('campaigns.show.status.thank_you'),
          cssClass: 'bg-gray-600 text-gray-150'
        }
      }
      if (isPreLaunchCampaign.value) {
        return {
          message: t('campaigns.show.status.not_started'),
          detail: t('campaigns.show.status.coming_soon'),
          cssClass: 'bg-blue-900/20 text-blue-300 border border-blue-500/30'
        }
      }
      return { message: null, detail: null, cssClass: '' }
    })

    // 從 campaignData 中提取創作者資訊 (添加null檢查)
    const creatorData = computed(() => {
      if (!props.campaignData) {
        return {
          brand_name: '',
          avatar_url: '',
          initials: '',
          projects_count: 0,
          description: '',
          social_links: []
        }
      }

      return {
        brand_name: props.campaignData.brand_name || '',
        avatar_url: props.campaignData.creator_avatar || '',
        initials: (props.campaignData.brand_name || '').charAt(0).toUpperCase(),
        projects_count: props.campaignData.projects_count || 0,
        description: props.campaignData.biography || '',
        social_links: [
          { platform: 'facebook', url: props.campaignData.facebook_url },
          { platform: 'instagram', url: props.campaignData.instagram_url },
          { platform: 'x', url: props.campaignData.x_url },
          { platform: 'youtube', url: props.campaignData.youtube_url },
          { platform: 'patreon', url: props.campaignData.patreon_url },
          { platform: 'portfolio', url: props.campaignData.portfolio_url }
        ].filter(link => link.url)
      }
    })

    // 當 avatar URL 改變時重置錯誤狀態
    watch(() => creatorData.value.avatar_url, () => {
      avatarLoadError.value = false
    })

    // 處理 reward 圖片 URL
    const getRewardImageUrl = (reward) => {
      if (!reward || !reward.cover_image) return null

      // 如果 cover_image 是字串，直接返回
      if (typeof reward.cover_image === 'string') {
        return reward.cover_image
      }

      // 如果是 CarrierWave 物件，嘗試不同的 URL 格式
      if (reward.cover_image.x2?.url) {
        return reward.cover_image.x2.url
      }

      if (reward.cover_image.x1?.url) {
        return reward.cover_image.x1.url
      }

      if (reward.cover_image.url) {
        return reward.cover_image.url
      }

      return null
    }

    // 處理圖片載入錯誤
    const handleImageError = (event) => {
      console.warn('Reward image failed to load:', event.target.src)
      // 可以在這裡設置預設圖片或其他處理
    }

    // 格式化日期為 "Sep 2024" 格式
    const formatDate = (dateString) => {
      if (!dateString) return 'TBD'
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short'
        })
      } catch (error) {
        console.warn('Date formatting error:', error)
        return 'TBD'
      }
    }

    // 數量選擇器方法
    const getRewardQuantity = (rewardId) => {
      return injectedRewardQuantities.value[rewardId] || 0
    }

    const incrementQuantity = (rewardId) => {
      const current = injectedRewardQuantities.value[rewardId] || 0
      updateRewardQuantity?.(rewardId, current + 1)
    }

    const decrementQuantity = (rewardId) => {
      const current = injectedRewardQuantities.value[rewardId] || 0
      if (current > 0) {
        updateRewardQuantity?.(rewardId, current - 1)
      }
    }

    const updateQuantity = (rewardId, value) => {
      const numValue = parseInt(value) || 0
      if (numValue >= 0) {
        updateRewardQuantity?.(rewardId, numValue)
      }
    }

    const navigateToComments = () => {
      window.location.hash = 'comments'
    }

    return {
      creatorData,
      avatarLoadError,
      getRewardImageUrl,
      handleImageError,
      formatDate,
      getRewardQuantity,
      incrementQuantity,
      decrementQuantity,
      updateQuantity,
      navigateToComments,
      shouldDisableControls,
      isCampaignEnded,
      isPreLaunchCampaign,
      canPledge,
      campaignStatus,
      formatCurrency
    }
  }
}
</script>
